import H2 from "components/atoms/H2"
import ScrollAnimate from "components/atoms/common/ScrollAnimate"
import QuoteIcon from "images/quote.svg"
import useTranslations from "hooks/useTranslations"

/**
 * Customer quotes, per Essi's Tab 3 spec.
 *
 * All three speakers are Finnish, so the page must not imply they are UK
 * schools. Each attribution carries its city, which does that work honestly.
 *
 * Cards size to their content rather than stretching to a shared height: the
 * quotes run from ~180 to ~700 characters, and equal-height cards would leave
 * the shortest two-thirds empty.
 */
export default function UkTestimonials() {
  const {
    ukSchoolsTestimonialsTitle,
    ukSchoolsTestimonialQuotes,
    ukSchoolsTestimonialAuthors,
  } = useTranslations()

  const quotes = ukSchoolsTestimonialQuotes || []
  const authors = ukSchoolsTestimonialAuthors || []

  if (!quotes.length) return null

  // "Name, Role, City" -> name on its own line, the rest beneath it.
  const splitAuthor = (author: string) => {
    const [name, ...rest] = (author || "").split(",")
    return { name: name.trim(), role: rest.join(",").trim() }
  }

  return (
    <div className="bg-zinc-800 py-16 lg:py-24">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <H2
          size="lg"
          className="m-0 mb-8 whitespace-pre-line text-left font-extrabold tracking-tight lg:mb-10"
          theme="dark"
        >
          {ukSchoolsTestimonialsTitle}
        </H2>

        <ul className="m-0 grid list-none grid-cols-1 items-start gap-8 p-0 lg:grid-cols-3">
          {quotes.map((quote, index) => {
            const { name, role } = splitAuthor(authors[index])
            return (
              <ScrollAnimate key={quote.slice(0, 40)} delay={index * 0.08}>
                <li
                  className={[
                    "relative rounded-3xl bg-zinc-900/60 p-8",
                    // Depth on a dark ground comes from a lighter surface and a
                    // 1px inset highlight along the top edge, the way a raised
                    // surface catches light. A drop shadow alone is invisible here.
                    "shadow-[0_1px_2px_rgba(0,0,0,0.35),0_12px_28px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.07)]",
                  ].join(" ")}
                >
                  <QuoteIcon
                    aria-hidden="true"
                    className="mb-5 h-9 w-9 text-kkm-lavender opacity-80"
                  />

                  <blockquote className="m-0">
                    <p className="m-0 text-base leading-relaxed text-zinc-200">
                      {quote}
                    </p>
                  </blockquote>

                  <div className="mt-6 border-t border-white/10 pt-5">
                    <cite className="not-italic">
                      <span className="block font-display text-base font-bold text-zinc-100">
                        {name}
                      </span>
                      {role ? (
                        <span className="mt-1 block text-sm leading-snug text-zinc-400">
                          {role}
                        </span>
                      ) : null}
                    </cite>
                  </div>
                </li>
              </ScrollAnimate>
            )
          })}
        </ul>
      </section>
    </div>
  )
}
